import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DxButtonModule, DxDateBoxModule, DxListModule, DxSchedulerModule, DxScrollViewModule, DxTextAreaModule, DxTextBoxModule } from 'devextreme-angular';
import { ApplicationService } from '../../services/application.service';
import { BehaviorSubject, map, Observable, switchMap } from 'rxjs';
import { Application } from '../../interfaces/application.interface';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { MaintenanceService } from '../../services/maintenance.service';
import { isNullOrWhitespace, pathJoin, showErrorSwal } from '@asamco/utils';
import { ApplicationTypeAL } from '../../enums/ApplicationTypeAL.enum';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-admin-link-maintenance',
  standalone: true,
  imports: [DxTextAreaModule, DxTextBoxModule, DxDateBoxModule, FormsModule, ReactiveFormsModule, DxListModule, AsyncPipe, DxListModule, DxSchedulerModule, NgFor, NgIf, DxScrollViewModule, DxButtonModule],
  templateUrl: './admin-link-maintenance.component.html',
  styleUrls: ['./admin-link-maintenance.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminLinkMaintenanceComponent {
  maintenanceForm: FormGroup;
  isLoading: boolean = false;

  selectedKeys: number[] = [];
  appointments: any[] = []
  companyTimezone: 'utc';
  public imgUrl = '';
  selectedApps: number[] = [];
  private selectedApps$ = new BehaviorSubject<number[]>([]);


  public applications$: Observable<Application[]>;

  public tasks: any[] = [
    { id: 1, text: 'Prepare 2016 Financial' },
    { id: 2, text: 'Prepare 2016 Marketing Plan' }
  ];

  constructor(private fb: FormBuilder, private applicationService: ApplicationService, private maintenanceService: MaintenanceService, private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    // Initialize the form
    this.maintenanceForm = this.fb.group({
      enableMaintenanceModeFrom: [null],
      enableMaintenanceModeTo: [null],
      enableMaintenanceModeMessageFrom: [null],
      maintenanceModeRecurrenceRule: [''],
      maintenanceModeRecurrenceRuleException: [''],
      maintenanceModeMessageShort: [''],
      maintenanceModeMessageLong: ['']
    });

    // Load applications and maintenance data sequentially
    this.loadData();

    this.imgUrl = pathJoin([window['deployUrl'], '/assets/img/']);

    this.applications$ = this.applicationService.get().pipe(
      map(data => {
        data.sort((a, b) => {
          return a.displayName.toLowerCase() < b.displayName.toLowerCase() ? -1 : 1;
        });
        return data;
      })
    );
  }

  public selected(id: number): boolean {
    return this.selectedApps.some(app => app === id);
  }

  filter(applications: Application[]): Application[] {
    // Define a list of whitelisted names
    const whitelistNames = ['Autotask', 'AutotaskRest', 'BusinessCentral', 'ConnectWise', 'ExactOnline', 'QuickBooks', 'WeClapp', 'Xero', 'Global', 'EntryPoint','E-Conomic'];

    return applications.filter(application => whitelistNames.includes(application.name)
    );
  }

  public getUrl(subUrl: string): string {
    if (!isNullOrWhitespace(this.imgUrl) && !isNullOrWhitespace(subUrl)) {
      return pathJoin([this.imgUrl, subUrl]);
    }
    return '';
  }

  /** Adds an app id to the selected App id array */
  public addToFilter(id: number): void {
    if (this.selectedApps.some(app => app === id)) {
      this.selectedApps = this.selectedApps.filter(app => app !== id);
    } else {
      this.selectedApps.push(id);
    }

    this.selectedApps$.next(this.selectedApps);
  }

  loadData(): void {
    this.isLoading = true; // Indicate loading state

    // First, load applications, then maintenance data
    this.applications$ = this.applicationService.get(); // Assign directly
    this.applications$.pipe(
      switchMap((applications) => {
        // Process applications if needed
        return this.maintenanceService.getConfiguration(); // Load maintenance data next
      })
    ).subscribe({
      next: (data) => {
        // Patch the form with the loaded maintenance data
        this.maintenanceForm.patchValue({
          enableMaintenanceModeFrom: data.enableMaintenanceModeFrom,
          enableMaintenanceModeTo: data.enableMaintenanceModeTo,
          enableMaintenanceModeMessageFrom: data.enableMaintenanceModeMessageFrom,
          maintenanceModeRecurrenceRule: data.maintenanceModeRecurrenceRule,
          maintenanceModeRecurrenceRuleException: data.maintenanceModeRecurrenceRuleException,
          maintenanceModeMessageShort: data.maintenanceModeMessageShort,
          maintenanceModeMessageLong: data.maintenanceModeMessageLong
        });

        if (data.maintenanceModeApplicationIds) {
          // Set the selected keys
          this.selectedKeys = data.maintenanceModeApplicationIds
            .split(',')
            .map((key) => parseInt(key, 10));

          this.selectedKeys.forEach((key) => { this.addToFilter(key) });
        }

        if (data.enableMaintenanceModeFrom && data.enableMaintenanceModeTo && data.maintenanceModeRecurrenceRule) {
          this.appointments.push({
            text: 'Maintenance',
            startDate: new Date(data.enableMaintenanceModeFrom),
            endDate: new Date(data.enableMaintenanceModeTo),
            recurrenceRule: data.maintenanceModeRecurrenceRule,
            disabled: true
          })
        }
      },
      error: (err) => {
        showErrorSwal(err); // Handle error
      },
      complete: () => {
        this.cdr.detectChanges();
        this.isLoading = false; // Stop loading indicator
      }
    });
  }

  public selectAll(applications: any[]): void {
    this.selectedApps = applications.map(app => app.id); // Select all application IDs
    this.selectedApps$.next(this.selectedApps);
  }

  public deselectAll(): void {
    this.selectedApps = []; // Clear the selected apps
    this.selectedApps$.next(this.selectedApps);
  }

  saveMaintenance(): void {
    if (this.maintenanceForm.valid) {
      const formValues = this.maintenanceForm.value;
      console.log('Saving maintenance data:', formValues);

      // Convert the selected keys to a comma-separated string
      formValues.maintenanceModeApplicationIds = this.selectedApps$.value.join(',');

      // Simulate saving the data (e.g., API call)
      this.isLoading = true;

      this.maintenanceService.updateConfiguration(formValues).subscribe({
        next: (e) => Swal.fire('Success', 'Maintenance configuration saved successfully!', 'success'),
        error: (err) => showErrorSwal(err),
      });
    } else {
      console.error('Form is invalid!');
    }
  }

}
